// src/features/ricevimento/RicevimentoMerci.jsx
import React, { useState, useEffect, useRef } from "react";
import Page from "../../components/Page";
import { useNavigate } from "react-router-dom";
import {
  Truck,
  PackageCheck,
  AlertOctagon,
  FileText,
  Camera,
  Search,
  UserPlus,
  Calendar as CalendarIcon,
  Thermometer,
  ThumbsUp,
  ThumbsDown,
  Save,
  RefreshCw,
  Download,
  X,
  Check,
  Tag,
  ArrowRight,
  Plus,
  Trash2,
  Layers3,
} from "lucide-react";

const TIPOLOGIE_PRODOTTO = ["ALTRO", "CARNE", "PESCE", "LATTICINI"];

const CATEGORIE_CONSERVAZIONE = [
  "REFRIGERATI",
  "SURGELATI",
  "SECCHI",
  "ALTRO",
];

const EMPTY_ROW = {
  tipologia_prodotto: "ALTRO",
  categoria_conservazione: "REFRIGERATI",
  descrizione: "",
  temperatura: "",
  esito: "CONFORME",
  note: "",
  is_selvaggina: 0,
  etichette_paths: [],
};

export default function RicevimentoMerci() {
  const navigate = useNavigate();
  const [lastSavedId, setLastSavedId] = useState(null);

  const [ocrEnabled, setOcrEnabled] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch("/api/me.php", { credentials: "include" })
      .then((r) => r.json())
      .then((me) => {
        if (!alive) return;
        setOcrEnabled(!!me?.features?.ddtOcrEnabled);
      })
      .catch(() => {
        if (alive) setOcrEnabled(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const [activeTab, setActiveTab] = useState("ricevimento");

  const [form, setForm] = useState({
    ddt: "",
    data: new Date().toISOString().slice(0, 10), // data ricezione
    data_documento_ddt: "", // data documento letta da OCR
    fornitore: "",
    ddt_file_path: "",
    righe: [{ ...EMPTY_ROW }],
  });

  const [ncPrefill, setNcPrefill] = useState({
    ricevimentoId: null,
    fornitoreId: null,
    fornitoreNome: "",
    ddt: "",
    ddtFilePath: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [records, setRecords] = useState([]);

  const [ddtFile, setDdtFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  const [fornitoriSuggeriti, setFornitoriSuggeriti] = useState([]);
  const [showSuggerimenti, setShowSuggerimenti] = useState(false);
  const [fornitoreQuery, setFornitoreQuery] = useState("");

  const fornitoreInputRef = useRef(null);

  const [fornitorePanelOpen, setFornitorePanelOpen] = useState(false);
  const [fornitoreLoading, setFornitoreLoading] = useState(false);
  const [fornitoreDetails, setFornitoreDetails] = useState({
    id: null,
    nome: "",
    piva_cf: "",
    indirizzo: "",
    telefono: "",
    email: "",
    note: "",
  });

  const [etichetteUploadingRow, setEtichetteUploadingRow] = useState(null);

  const loadLast5 = () => {
    fetch("/api/ricevimento_merci_list.php?period=month", {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => setRecords(Array.isArray(data?.items) ? data.items : []))
      .catch(() => setRecords([]));
  };

  useEffect(() => {
    loadLast5();
  }, []);

  useEffect(() => {
    const q = fornitoreQuery.trim();
    if (q.length < 2) {
      setFornitoriSuggeriti([]);
      setShowSuggerimenti(false);
      return;
    }

    const ac = new AbortController();

    fetch(`/api/fornitori_search.php?q=${encodeURIComponent(q)}`, {
      credentials: "include",
      signal: ac.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items
          .map((x, idx) => ({
            id: x?.id ?? idx,
            nome: x?.nome ?? "",
          }))
          .filter((x) => (x.nome || "").trim().length > 0);

        setFornitoriSuggeriti(normalized);
        setShowSuggerimenti(normalized.length > 0);
      })
      .catch(() => {
        setFornitoriSuggeriti([]);
        setShowSuggerimenti(false);
      });

    return () => ac.abort();
  }, [fornitoreQuery]);

  const pickFornitore = async (f) => {
    setForm((prev) => ({ ...prev, fornitore: f.nome || "" }));
    setFornitoreQuery(f.nome || "");
    setShowSuggerimenti(false);
    setFornitoriSuggeriti([]);

    if (f?.id) {
      try {
        setFornitoreLoading(true);
        const r = await fetch(`/api/fornitore_get.php?id=${f.id}`, {
          credentials: "include",
        });
        const data = await r.json();
        const item = data?.fornitore || null;

        setFornitoreDetails({
          id: item?.id || f.id,
          nome: item?.nome || f.nome || "",
          piva_cf: item?.piva_cf || "",
          indirizzo: item?.indirizzo || "",
          telefono: item?.telefono || "",
          email: item?.email || "",
          note: item?.note || "",
        });
      } catch {
        setFornitoreDetails((p) => ({ ...p, id: f.id, nome: f.nome || "" }));
      } finally {
        setFornitoreLoading(false);
      }
    }
  };

  const openFornitorePanel = () => {
    setFornitorePanelOpen(true);
    setFornitoreDetails((p) => ({
      ...p,
      nome: form.fornitore || p.nome || "",
    }));
  };

  const saveFornitoreDetails = async () => {
    try {
      setFornitoreLoading(true);
      const res = await fetch("/api/fornitore_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(fornitoreDetails),
      });
      const data = await res.json();
      alert(data?.message || "Salvato");
      if (data?.id) setFornitoreDetails((p) => ({ ...p, id: data.id }));
    } catch {
      alert("Errore salvataggio fornitore");
    } finally {
      setFornitoreLoading(false);
    }
  };

  const autoRegisterFornitore = async () => {
    const nome = (form.fornitore || "").trim();
    if (!nome) {
      alert("Inserisci prima il nome del fornitore.");
      return;
    }

    try {
      setFornitoreLoading(true);

      const getRes = await fetch(
        `/api/fornitore_get.php?nome=${encodeURIComponent(nome)}`,
        {
          credentials: "include",
        }
      );
      const getData = await getRes.json();
      const item = getData?.fornitore || null;

      if (item) {
        setFornitoreDetails({
          id: item.id || null,
          nome: item.nome || nome,
          piva_cf: item.piva_cf || "",
          indirizzo: item.indirizzo || "",
          telefono: item.telefono || "",
          email: item.email || "",
          note: item.note || "",
        });

        setForm((prev) => ({
          ...prev,
          fornitore: item.nome || nome,
        }));
        setFornitoreQuery(item.nome || nome);

        alert("Fornitore trovato e associato automaticamente.");
        return;
      }

      const saveRes = await fetch("/api/fornitore_save.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          nome,
          piva_cf: "",
          indirizzo: "",
          telefono: "",
          email: "",
          note: "",
        }),
      });

      const saveData = await saveRes.json();

      if (!saveData?.success) {
        throw new Error(saveData?.message || "Errore salvataggio fornitore");
      }

      setFornitoreDetails({
        id: saveData.id || null,
        nome,
        piva_cf: "",
        indirizzo: "",
        telefono: "",
        email: "",
        note: "",
      });

      setFornitoreQuery(nome);
      alert("Fornitore creato automaticamente.");
    } catch (e) {
      alert(e?.message || "Errore registrazione automatica fornitore");
    } finally {
      setFornitoreLoading(false);
    }
  };

  const handleDdtFileChange = async (file) => {
    setDdtFile(file);
    if (!file) return;

    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("ddt_file", file);

      const res = await fetch("/api/ddt_ocr.php", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();

      if (data?.success) {
        const path = data.ddt_file_path || data.file_path || "";
        const righeOcr = Array.isArray(data?.righe_ocr) ? data.righe_ocr : [];

        setForm((prev) => ({
          ...prev,
          ddt: data.ddt || prev.ddt,
          data_documento_ddt:
            data.data_documento || data.data || prev.data_documento_ddt || "",
          fornitore: data.fornitore || prev.fornitore,
          ddt_file_path: path || prev.ddt_file_path,
          righe:
            righeOcr.length > 0
              ? righeOcr.map((r) => ({
                ...EMPTY_ROW,
                tipologia_prodotto: r?.tipologia_prodotto || "ALTRO",
                categoria_conservazione:
                  r?.categoria_conservazione || "ALTRO",
                descrizione: r?.descrizione || "",
                temperatura: "",
                esito: "CONFORME",
                note: "",
                is_selvaggina: 0,
                etichette_paths: [],
              }))
              : prev.righe,
        }));

        if (data?.fornitore) setFornitoreQuery(data.fornitore);

        if (data?.warning) {
          alert(data.warning);
        }
      } else {
        alert(data?.message || "Upload/OCR non riuscito");
      }
    } catch {
      alert("Errore upload/OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const updateRiga = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      righe: prev.righe.map((r, i) => {
        if (i !== index) return r;

        const next = { ...r, ...patch };

        if (next.categoria_conservazione === "SECCHI") {
          next.temperatura = "";
        }

        if (next.tipologia_prodotto !== "CARNE") {
          next.is_selvaggina = 0;
        }

        return next;
      }),
    }));
  };

  const addRiga = () => {
    setForm((prev) => ({
      ...prev,
      righe: [...prev.righe, { ...EMPTY_ROW }],
    }));
  };

  const removeRiga = (index) => {
    setForm((prev) => ({
      ...prev,
      righe:
        prev.righe.length > 1
          ? prev.righe.filter((_, i) => i !== index)
          : prev.righe,
    }));
  };

  const handleEtichetteUploadForRow = async (rowIndex, files) => {
    if (!files || files.length === 0) return;
    setEtichetteUploadingRow(rowIndex);

    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("files[]", f));

      const res = await fetch("/api/etichette_upload.php", {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await res.json();

      if (data?.success && Array.isArray(data.paths)) {
        setForm((prev) => ({
          ...prev,
          righe: prev.righe.map((r, i) =>
            i === rowIndex
              ? {
                ...r,
                etichette_paths: [
                  ...(Array.isArray(r.etichette_paths)
                    ? r.etichette_paths
                    : []),
                  ...data.paths,
                ],
              }
              : r
          ),
        }));
      } else {
        const msg = data?.errors?.length
          ? `${data?.message || "Errore upload etichette"
          }\n\n${data.errors.join("\n")}`
          : data?.message || "Errore upload etichette";
        alert(msg);
      }
    } catch {
      alert("Errore di rete durante il caricamento delle etichette");
    } finally {
      setEtichetteUploadingRow(null);
    }
  };

  const removeEtichettaFromRow = (rowIndex, pathIndex) => {
    setForm((prev) => ({
      ...prev,
      righe: prev.righe.map((r, i) =>
        i === rowIndex
          ? {
            ...r,
            etichette_paths: (r.etichette_paths || []).filter(
              (_, pIdx) => pIdx !== pathIndex
            ),
          }
          : r
      ),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (!Array.isArray(form.righe) || form.righe.length === 0) {
        alert("Aggiungi almeno una tipologia di prodotto.");
        setSubmitting(false);
        return;
      }

      for (const [idx, r] of form.righe.entries()) {
        const tempRaw = String(r.temperatura ?? "").trim();
        const isSecchi = r.categoria_conservazione === "SECCHI";

        if (!isSecchi && tempRaw === "") {
          alert(`Inserisci la temperatura nella riga ${idx + 1}.`);
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        ddt: form.ddt,
        data: form.data,
        data_documento_ddt: form.data_documento_ddt || null,
        fornitore: form.fornitore,
        ddt_file_path: form.ddt_file_path,
        righe: form.righe.map((r) => {
          const isSecchi = r.categoria_conservazione === "SECCHI";
          const temp = String(r.temperatura ?? "").trim().replace(",", ".");

          return {
            ...r,
            temperatura: isSecchi ? null : temp || null,
          };
        }),
      };

      const res = await fetch("/api/ricevimento_merci.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!data?.success) {
        alert(data?.message || "Errore durante il salvataggio");
        setSubmitting(false);
        return;
      }

      if (data?.id) setLastSavedId(data.id);
      alert(data?.message || "Operazione completata");

      loadLast5();

      const hasNonConforme = payload.righe.some(
        (r) => r.esito === "NON CONFORME"
      );

      if (hasNonConforme) {
        const ricevimentoId = data?.id || null;

        setNcPrefill({
          ricevimentoId,
          fornitoreId: fornitoreDetails?.id || null,
          fornitoreNome: payload.fornitore || "",
          ddt: payload.ddt || "",
          ddtFilePath: payload.ddt_file_path || "",
        });

        setActiveTab("nc");

        setForm((prev) => ({
          ...prev,
          righe: [{ ...EMPTY_ROW }],
        }));

        setSubmitting(false);
        return;
      }

      setForm({
        ddt: "",
        data: new Date().toISOString().slice(0, 10),
        data_documento_ddt: "",
        fornitore: "",
        ddt_file_path: "",
        righe: [{ ...EMPTY_ROW }],
      });

      setDdtFile(null);
      setFornitoriSuggeriti([]);
      setShowSuggerimenti(false);
      setFornitoreQuery("");
      setFornitoreDetails({
        id: null,
        nome: "",
        piva_cf: "",
        indirizzo: "",
        telefono: "",
        email: "",
        note: "",
      });
    } catch {
      alert("Errore di rete");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClickUpgrade = () => {
    alert(
      "La funzione OCR non è attiva nel tuo piano. Contattaci per abilitarla."
    );
  };

  const clearAfterNcSaved = () => {
    setNcPrefill({
      ricevimentoId: null,
      fornitoreId: null,
      fornitoreNome: "",
      ddt: "",
      ddtFilePath: "",
    });

    setForm((prev) => ({
      ...prev,
      ddt: "",
      data_documento_ddt: "",
      fornitore: "",
      ddt_file_path: "",
    }));
  };

  const startNcFromRecord = (r) => {
    setNcPrefill({
      ricevimentoId: r?.id || null,
      fornitoreId: fornitoreDetails?.id || null,
      fornitoreNome: r?.fornitore || "",
      ddt: r?.ddt || "",
      ddtFilePath: r?.ddt_file_path || "",
    });
    setActiveTab("nc");
  };

  return (
    <Page title="Ricevimento Merci" back>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 space-y-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-200/60 pb-2">
          <div>
            <h2 className="text-3xl md:text-4xl text-gray-800 font-light tracking-tight">
              {activeTab === "ricevimento"
                ? "Ricevimento Merci"
                : "Merci Non Conformi"}
            </h2>
            <p className="text-gray-400 text-sm mt-1 font-light">
              {activeTab === "ricevimento"
                ? "Registra i documenti di trasporto e verifica la conformità."
                : "Gestisci le non conformità e le azioni correttive sui prodotti."}
            </p>
          </div>

          <div className="flex items-center bg-white p-1 rounded-[24px] shadow-sm border border-gray-200 w-full md:w-auto shrink-0 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab("ricevimento")}
              className={[
                "flex-1 px-6 py-2.5 rounded-[20px] text-sm font-bold transition-all flex items-center justify-center gap-2 whitespace-nowrap",
                activeTab === "ricevimento"
                  ? "bg-[#003B71] text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-50",
              ].join(" ")}
            >
              <PackageCheck className="w-4 h-4" />
              Ricevimento
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("nc")}
              className={[
                "flex-1 px-6 py-2.5 rounded-[20px] text-sm font-bold transition-all flex items-center justify-center gap-2 whitespace-nowrap",
                activeTab === "nc"
                  ? "bg-[#003B71] text-white shadow-md"
                  : "text-gray-500 hover:bg-gray-50",
              ].join(" ")}
            >
              <AlertOctagon className="w-4 h-4" />
              Non Conformità
            </button>
          </div>
        </div>

        {activeTab === "ricevimento" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <section className="lg:col-span-7 space-y-6">
              <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#003B71]/10 text-[#003B71] flex items-center justify-center shrink-0">
                      <Truck className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-gray-800">
                        Ricevimento Merci
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Scatta foto o carica il DDT (foto/PDF). Se OCR attivo,
                        estraiamo automaticamente numero DDT, data documento,
                        fornitore e, quando disponibili, anche le righe prodotto.
                      </p>
                      {ocrEnabled === null && (
                        <p className="text-xs text-gray-400 mt-1">
                          Verifica piano OCR…
                        </p>
                      )}
                    </div>
                  </div>

                  {ocrEnabled === false && (
                    <button
                      type="button"
                      onClick={handleClickUpgrade}
                      className="px-4 py-2 rounded-[18px] bg-[#FFF7ED] border border-[#FA8216]/30 text-[#FA8216] font-bold text-sm hover:bg-[#FFF7ED] shadow-sm"
                    >
                      Attiva OCR
                    </button>
                  )}
                </div>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden"
              >
                <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#003B71]/10 text-[#003B71] flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  Dati Documento
                </h3>

                <div className="mb-6 group">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#FA8216]/30 rounded-[20px] bg-[#FFF7ED]/30 hover:bg-[#FFF7ED] cursor-pointer transition-all">
                    <div className="flex flex-col items-center justify-center pt-3 pb-4">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-[#FA8216] shadow-sm mb-2 group-hover:scale-110 transition-transform">
                        <Camera className="w-5 h-5" />
                      </div>
                      <p className="mb-0.5 text-sm text-gray-600 font-medium">
                        Scatta foto o carica DDT
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {ocrEnabled === false
                          ? "OCR non attivo nel piano"
                          : "OCR: estrazione dati automatica"}
                      </p>
                    </div>

                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) =>
                        handleDdtFileChange(e.target.files?.[0] || null)
                      }
                    />
                  </label>

                  {ocrLoading && (
                    <div className="mt-2 text-xs text-gray-400">
                      OCR in corso…
                    </div>
                  )}

                  {ddtFile?.name && (
                    <div className="mt-2 text-xs text-gray-500">
                      File selezionato:{" "}
                      <span className="font-semibold">{ddtFile.name}</span>
                    </div>
                  )}

                  {form.ddt_file_path && (
                    <div className="mt-2 text-xs text-gray-500">
                      File salvato:{" "}
                      <span className="font-semibold">{form.ddt_file_path}</span>
                    </div>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                        N° DDT
                      </label>
                      <input
                        type="text"
                        placeholder="Es. 2024/A-12"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
                        value={form.ddt}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, ddt: e.target.value }))
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                        Data Ricezione
                      </label>
                      <div className="relative">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="date"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
                          value={form.data}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, data: e.target.value }))
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                        Data documento DDT
                      </label>
                      <div className="relative">
                        <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          type="date"
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
                          value={form.data_documento_ddt}
                          onChange={(e) =>
                            setForm((p) => ({
                              ...p,
                              data_documento_ddt: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <p className="text-[11px] text-gray-400 mt-1 ml-1">
                        Campo separato dalla data di ricezione merce.
                      </p>
                    </div>

                    <div className="flex items-end">
                      <div className="w-full rounded-[20px] border border-[#003B71]/10 bg-[#003B71]/[0.03] p-4 text-sm text-gray-600">
                        Se l’OCR legge una data dal documento, viene proposta qui
                        come data del DDT, non come data di ricezione.
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                      Fornitore
                    </label>

                    <div className="relative flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <input
                          ref={fornitoreInputRef}
                          type="text"
                          placeholder="Cerca fornitore..."
                          className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
                          value={form.fornitore}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((p) => ({ ...p, fornitore: v }));
                            setFornitoreQuery(v);
                          }}
                          onFocus={() => {
                            if (fornitoriSuggeriti.length > 0)
                              setShowSuggerimenti(true);
                          }}
                          onBlur={() =>
                            setTimeout(() => setShowSuggerimenti(false), 150)
                          }
                        />
                      </div>

                      <button
                        type="button"
                        onClick={autoRegisterFornitore}
                        disabled={fornitoreLoading}
                        className="px-4 rounded-[20px] border border-gray-200 text-[#003B71] bg-white hover:bg-gray-50 flex items-center justify-center shadow-sm disabled:opacity-60"
                        title="Registra automaticamente il fornitore"
                      >
                        <UserPlus className="w-5 h-5" />
                      </button>

                      <button
                        type="button"
                        onClick={openFornitorePanel}
                        className="px-4 rounded-[20px] border border-gray-200 text-gray-600 bg-white hover:bg-gray-50 flex items-center justify-center shadow-sm"
                        title="Apri anagrafica fornitore"
                      >
                        <FileText className="w-5 h-5" />
                      </button>
                    </div>

                    {showSuggerimenti && fornitoriSuggeriti.length > 0 && (
                      <div className="absolute z-20 mt-2 w-full bg-white border border-gray-200 rounded-[18px] shadow-sm overflow-hidden">
                        {fornitoriSuggeriti.map((f, idx) => (
                          <button
                            key={`${f.id}-${idx}`}
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickFornitore(f);
                            }}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm text-gray-700 flex items-center gap-2"
                          >
                            <span className="w-2 h-2 rounded-full bg-[#003B71]/40" />
                            <span className="font-semibold">{f.nome}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    <p className="text-[11px] text-gray-400 mt-2 ml-1">
                      Il pulsante con l’omino tenta prima di trovare il fornitore
                      già esistente e, se non lo trova, lo crea automaticamente.
                    </p>
                  </div>

                  <hr className="border-gray-100" />

                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h4 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <Layers3 className="w-5 h-5 text-[#003B71]" />
                        Tipologie prodotto nello stesso DDT
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Aggiungi una riga per ogni gruppo di merce con temperatura
                        o controllo diverso. Se l’OCR riconosce le righe prodotto,
                        verranno precompilate automaticamente.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={addRiga}
                      className="px-4 py-2 rounded-[18px] bg-[#003B71] text-white font-bold text-sm hover:bg-[#002B5B] shadow-sm flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Aggiungi
                    </button>
                  </div>

                  <div className="space-y-5">
                    {form.righe.map((riga, idx) => {
                      const showEtichette =
                        riga.tipologia_prodotto === "CARNE" ||
                        riga.tipologia_prodotto === "PESCE";

                      const isCarne = riga.tipologia_prodotto === "CARNE";
                      const isSecchi =
                        riga.categoria_conservazione === "SECCHI";

                      return (
                        <div
                          key={idx}
                          className="rounded-[24px] border border-gray-200 bg-gray-50/70 p-5 md:p-6 space-y-5"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#003B71] font-bold shadow-sm">
                                {idx + 1}
                              </div>
                              <div>
                                <div className="font-bold text-gray-800">
                                  Riga prodotto #{idx + 1}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Tipologia, conservazione, temperatura ed esito
                                </div>
                              </div>
                            </div>

                            {form.righe.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRiga(idx)}
                                className="h-10 px-4 rounded-[16px] border border-red-200 text-red-600 bg-white hover:bg-red-50 flex items-center gap-2 text-sm font-bold"
                              >
                                <Trash2 className="w-4 h-4" />
                                Rimuovi
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                Tipologia
                              </label>
                              <select
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] appearance-none"
                                value={riga.tipologia_prodotto}
                                onChange={(e) =>
                                  updateRiga(idx, {
                                    tipologia_prodotto: e.target.value,
                                    is_selvaggina:
                                      e.target.value === "CARNE"
                                          ? riga.is_selvaggina
                                          : 0,
                                  })
                                }
                              >
                                {TIPOLOGIE_PRODOTTO.map((tipo) => (
                                  <option key={tipo} value={tipo}>
                                    {tipo}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                Categoria conservazione
                              </label>
                              <select
                                className="w-full px-4 py-3 bg-white border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] appearance-none"
                                value={riga.categoria_conservazione}
                                onChange={(e) =>
                                  updateRiga(idx, {
                                    categoria_conservazione: e.target.value,
                                  })
                                }
                              >
                                {CATEGORIE_CONSERVAZIONE.map((cat) => (
                                  <option key={cat} value={cat}>
                                    {cat}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                              Descrizione prodotto
                            </label>
                            <input
                              type="text"
                              placeholder="Es. Mozzarella fiordilatte / Salmone fresco / Carne bovina..."
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
                              value={riga.descrizione}
                              onChange={(e) =>
                                updateRiga(idx, { descrizione: e.target.value })
                              }
                            />
                          </div>

                          {isCarne && (
                            <label className="flex items-start gap-4 p-4 bg-white rounded-[20px] border border-transparent hover:border-gray-200 cursor-pointer transition-all">
                              <div className="relative flex items-center">
                                <input
                                  type="checkbox"
                                  checked={!!riga.is_selvaggina}
                                  onChange={(e) => {
                                    const v = e.target.checked ? 1 : 0;
                                    updateRiga(idx, { is_selvaggina: v });
                                  }}
                                  className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border border-gray-300 shadow-sm transition-all checked:border-[#003B71] checked:bg-[#003B71] hover:border-[#003B71]"
                                />
                                <Check className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" />
                              </div>
                              <div>
                                <span className="font-bold text-gray-800 block text-sm">
                                  Selvaggina
                                </span>
                                <span className="text-xs text-gray-500 leading-tight block mt-1">
                                  Attiva questa opzione solo per la riga carne
                                  relativa a selvaggina da cacciatore autorizzato.
                                </span>

                                {riga.is_selvaggina ? (
                                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                                    <button
                                      type="button"
                                      className="px-4 py-2 rounded-[16px] bg-[#003B71] text-white font-bold shadow-sm hover:bg-[#002B5B] transition"
                                      onClick={() => {
                                        if (!lastSavedId) {
                                          alert(
                                            "Prima salva il ricevimento merci, poi compiliamo la dichiarazione selvaggina."
                                          );
                                          return;
                                        }
                                        navigate(`/selvaggina/${lastSavedId}`);
                                      }}
                                    >
                                      Compila dichiarazione cacciatore
                                    </button>

                                    {!lastSavedId && (
                                      <div className="text-xs text-[#FA8216] bg-[#FFF7ED] border border-[#FA8216]/20 rounded-[16px] px-3 py-2">
                                        Nota: la dichiarazione si collega al
                                        ricevimento. Salva prima il ricevimento.
                                      </div>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            </label>
                          )}

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-end">
                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                Temperatura (°C)
                              </label>
                              <div className="relative">
                                <Thermometer className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                                <input
                                  type="number"
                                  step="0.1"
                                  required={!isSecchi}
                                  disabled={isSecchi}
                                  placeholder={
                                    isSecchi
                                      ? "Non richiesta per prodotti secchi"
                                      : "Es. 4.0"
                                  }
                                  className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all disabled:bg-gray-100 disabled:text-gray-400"
                                  value={riga.temperatura}
                                  onChange={(e) =>
                                    updateRiga(idx, {
                                      temperatura: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              {isSecchi && (
                                <p className="text-[11px] text-gray-400 mt-1 ml-1">
                                  Per la categoria SECCHI la temperatura non è
                                  obbligatoria.
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                                Esito Controllo
                              </label>
                              <div className="flex bg-gray-100 p-1 rounded-[20px]">
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateRiga(idx, { esito: "CONFORME" })
                                  }
                                  className={[
                                    "flex-1 py-2.5 rounded-[16px] text-sm font-bold flex items-center justify-center gap-2 transition-all",
                                    riga.esito === "CONFORME"
                                      ? "bg-[#059669] text-white shadow-sm"
                                      : "text-gray-500 hover:text-gray-700",
                                  ].join(" ")}
                                >
                                  <ThumbsUp className="w-4 h-4" /> Conforme
                                </button>

                                <button
                                  type="button"
                                  onClick={() =>
                                    updateRiga(idx, { esito: "NON CONFORME" })
                                  }
                                  className={[
                                    "flex-1 py-2.5 rounded-[16px] text-sm font-bold flex items-center justify-center gap-2 transition-all",
                                    riga.esito === "NON CONFORME"
                                      ? "bg-[#EF4444] text-white shadow-sm"
                                      : "text-gray-500 hover:text-gray-700",
                                  ].join(" ")}
                                >
                                  <ThumbsDown className="w-4 h-4" /> Non conf.
                                </button>
                              </div>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                              Note riga
                            </label>
                            <textarea
                              rows={3}
                              placeholder="Annotazioni specifiche su questa tipologia di merce..."
                              className="w-full px-4 py-3 bg-white border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] resize-none"
                              value={riga.note}
                              onChange={(e) =>
                                updateRiga(idx, { note: e.target.value })
                              }
                            />
                          </div>

                          {showEtichette && (
                            <div className="rounded-[20px] border border-[#FA8216]/20 bg-[#FFF7ED]/40 p-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-sm font-bold text-gray-800">
                                    Etichette prodotti ({riga.tipologia_prodotto})
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    Carica le foto delle etichette di questa riga
                                    prodotto.
                                  </div>
                                </div>
                                {etichetteUploadingRow === idx ? (
                                  <span className="text-xs text-gray-400">
                                    Caricamento…
                                  </span>
                                ) : null}
                              </div>

                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                capture="environment"
                                className="mt-3 block w-full text-sm"
                                onChange={(e) =>
                                  handleEtichetteUploadForRow(
                                    idx,
                                    e.target.files
                                  )
                                }
                                disabled={etichetteUploadingRow === idx}
                              />

                              {Array.isArray(riga.etichette_paths) &&
                                riga.etichette_paths.length > 0 && (
                                  <ul className="mt-3 text-xs text-gray-700 list-disc pl-5 space-y-2">
                                    {riga.etichette_paths.map((p, pIdx) => (
                                      <li
                                        key={`${p}-${pIdx}`}
                                        className="flex items-center justify-between gap-3"
                                      >
                                        <span className="break-all">{p}</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeEtichettaFromRow(idx, pIdx)
                                          }
                                          className="shrink-0 text-red-600 font-bold"
                                        >
                                          Rimuovi
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={addRiga}
                    className="w-full border border-dashed border-[#003B71]/40 text-[#003B71] bg-[#003B71]/[0.03] hover:bg-[#003B71]/[0.05] font-bold py-4 rounded-[20px] flex items-center justify-center gap-2 transition-all"
                  >
                    <Plus className="w-5 h-5" />
                    Aggiungi altra tipologia di prodotto
                  </button>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#003B71] hover:bg-[#002B5B] text-white font-bold text-lg py-4 rounded-[20px] shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-60 disabled:transform-none"
                  >
                    <Save className="w-6 h-6" />
                    {submitting ? "Salvataggio..." : "Salva Ricevimento"}
                  </button>
                </div>
              </form>

              {fornitorePanelOpen && (
                <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between gap-3 mb-6">
                    <div className="text-xl font-bold text-gray-800 flex items-center gap-2">
                      <UserPlus className="w-5 h-5 text-[#003B71]" />
                      Anagrafica Fornitore
                    </div>
                    <button
                      type="button"
                      onClick={() => setFornitorePanelOpen(false)}
                      className="h-10 px-4 rounded-[16px] border border-gray-200 text-gray-600 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <X className="w-4 h-4" /> Chiudi
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <InputSoft
                      placeholder="Nome"
                      value={fornitoreDetails.nome}
                      onChange={(e) =>
                        setFornitoreDetails((p) => ({
                          ...p,
                          nome: e.target.value,
                        }))
                      }
                    />
                    <InputSoft
                      placeholder="P.IVA / CF"
                      value={fornitoreDetails.piva_cf}
                      onChange={(e) =>
                        setFornitoreDetails((p) => ({
                          ...p,
                          piva_cf: e.target.value,
                        }))
                      }
                    />
                    <InputSoft
                      placeholder="Indirizzo"
                      value={fornitoreDetails.indirizzo}
                      onChange={(e) =>
                        setFornitoreDetails((p) => ({
                          ...p,
                          indirizzo: e.target.value,
                        }))
                      }
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <InputSoft
                        placeholder="Telefono"
                        value={fornitoreDetails.telefono}
                        onChange={(e) =>
                          setFornitoreDetails((p) => ({
                            ...p,
                            telefono: e.target.value,
                          }))
                        }
                      />
                      <InputSoft
                        placeholder="Email"
                        value={fornitoreDetails.email}
                        onChange={(e) =>
                          setFornitoreDetails((p) => ({
                            ...p,
                            email: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <textarea
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] resize-none min-h-[110px]"
                      placeholder="Note"
                      value={fornitoreDetails.note}
                      onChange={(e) =>
                        setFornitoreDetails((p) => ({
                          ...p,
                          note: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="mt-5 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={saveFornitoreDetails}
                      disabled={fornitoreLoading}
                      className="h-11 px-5 rounded-[18px] bg-[#003B71] text-white font-bold shadow-sm hover:bg-[#002B5B] disabled:opacity-60"
                    >
                      {fornitoreLoading ? "Salvataggio..." : "Salva Fornitore"}
                    </button>
                    <div className="text-xs text-gray-400">
                      Suggerimento: salva una volta e poi l’autocomplete troverà
                      subito il fornitore.
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="lg:col-span-5 space-y-6">
              <UltimeRegistrazioniCard
                records={records}
                onViewAll={() => {
                  window.location.href = "/archivio-ddt";
                }}
                onCreateNc={startNcFromRecord}
              />
              <StatsMeseCard records={records} />
            </section>
          </div>
        )}

        {activeTab === "nc" && (
          <NonConformitaScarti
            prefillRicevimentoId={ncPrefill.ricevimentoId}
            prefillFornitoreId={ncPrefill.fornitoreId || fornitoreDetails?.id}
            prefillFornitoreNome={ncPrefill.fornitoreNome || form?.fornitore}
            prefillDdt={ncPrefill.ddt || form?.ddt}
            prefillDdtFilePath={ncPrefill.ddtFilePath || form?.ddt_file_path}
            onSaved={() => {
              clearAfterNcSaved();
              loadLast5();
            }}
          />
        )}
      </div>
    </Page>
  );
}

/* ------------------------- NC COMPONENT ------------------------- */

function NonConformitaScarti({
  prefillRicevimentoId,
  prefillFornitoreId,
  prefillFornitoreNome,
  prefillDdt,
  prefillDdtFilePath,
  onSaved,
}) {
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [nc, setNc] = useState({
    origine: "RICEVUTA",
    tipo_nc: "TEMPERATURA",
    tipo_nc_altro: "",
    note: "",
    azione_correttiva: "RESTITUZIONE_FORNITORE",
    verifica_azione: "",
  });

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/merci_non_conformi_list.php?limit=50", {
        credentials: "include",
      });
      const data = await r.json();
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...nc,
        ricevimento_id: prefillRicevimentoId || null,
        fornitore_id: prefillFornitoreId || null,
        fornitore_nome: (prefillFornitoreNome || "").trim() || null,
        ddt: (prefillDdt || "").trim() || null,
        ddt_file_path: (prefillDdtFilePath || "").trim() || null,
      };

      const res = await fetch("/api/merci_non_conformi_create.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      alert(data?.message || "Operazione completata");

      setNc((p) => ({
        ...p,
        tipo_nc: "TEMPERATURA",
        tipo_nc_altro: "",
        note: "",
        azione_correttiva: "RESTITUZIONE_FORNITORE",
        verifica_azione: "",
      }));

      await loadList();
      if (typeof onSaved === "function") onSaved();
    } catch {
      alert("Errore di rete");
    } finally {
      setSaving(false);
    }
  };

  const tipoLabel = (t, altro) => {
    const map = {
      TEMPERATURA: "Temperatura",
      INSUDICIAMENTO: "Insudiciamento",
      CONFEZIONAMENTO: "Confezionamento",
      CORPI_ESTRANEI_MUFFE: "Corpi estranei / muffe",
      SCADENZA: "Scadenza",
      ALTRO: "Altro",
    };
    const base = map[t] || t;
    return t === "ALTRO" && altro ? `${base}: ${altro}` : base;
  };

  const azioneLabel = (a) => {
    const map = {
      RESTITUZIONE_FORNITORE: "Restituzione al fornitore",
      CONFINAMENTO: "Confinamento della merce",
      ELIMINAZIONE_SCARTO: "Eliminazione / Scarto",
    };
    return map[a] || a;
  };

  const badge = (t) => {
    if (t === "TEMPERATURA")
      return "border-[#EF4444] bg-red-50 text-[#EF4444]";
    if (t === "SCADENZA")
      return "border-orange-400 bg-orange-50 text-orange-600";
    return "border-[#EF4444] bg-red-50 text-[#EF4444]";
  };

  const openPdf = (id) =>
    window.open(`/api/merci_non_conformi_pdf_one.php?id=${id}`, "_blank");
  const openLabel = (id) =>
    window.open(`/api/merci_non_conformi_label_pdf.php?id=${id}`, "_blank");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <section className="lg:col-span-7 space-y-6">
        <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#EF4444]/10 text-[#EF4444] flex items-center justify-center shrink-0">
              <AlertOctagon className="w-5 h-5" />
            </div>
            Gestione Non Conformità
          </h3>

          <div className="mb-6 flex flex-wrap gap-2">
            <span className="px-3 py-2 rounded-[16px] bg-gray-50 border border-gray-200 text-gray-700 text-sm">
              Fornitore: <strong>{prefillFornitoreNome || "—"}</strong>
            </span>
            <span className="px-3 py-2 rounded-[16px] bg-gray-50 border border-gray-200 text-gray-700 text-sm">
              DDT: <strong>{prefillDdt || "—"}</strong>
            </span>
            <span className="px-3 py-2 rounded-[16px] bg-gray-50 border border-gray-200 text-gray-700 text-sm">
              Ricevimento ID: <strong>{prefillRicevimentoId || "—"}</strong>
            </span>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                Merce ricevuta o già presente?
              </label>
              <select
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444] appearance-none"
                value={nc.origine}
                onChange={(e) =>
                  setNc((p) => ({ ...p, origine: e.target.value }))
                }
              >
                <option value="RICEVUTA">Merce ricevuta</option>
                <option value="GIA_PRESENTE">Merce già presente</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                Descrizione della non conformità
              </label>
              <select
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444] appearance-none"
                value={nc.tipo_nc}
                onChange={(e) =>
                  setNc((p) => ({
                    ...p,
                    tipo_nc: e.target.value,
                    tipo_nc_altro:
                      e.target.value === "ALTRO" ? p.tipo_nc_altro : "",
                  }))
                }
              >
                <option value="TEMPERATURA">TEMPERATURA</option>
                <option value="INSUDICIAMENTO">INSUDICIAMENTO</option>
                <option value="CONFEZIONAMENTO">CONFEZIONAMENTO</option>
                <option value="CORPI_ESTRANEI_MUFFE">
                  PRESENZA DI CORPI ESTRANEI E/O MUFFE
                </option>
                <option value="SCADENZA">SCADENZA</option>
                <option value="ALTRO">ALTRO</option>
              </select>

              {nc.tipo_nc === "ALTRO" && (
                <input
                  className="mt-3 w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444]"
                  placeholder="Specifica Altro…"
                  value={nc.tipo_nc_altro}
                  onChange={(e) =>
                    setNc((p) => ({ ...p, tipo_nc_altro: e.target.value }))
                  }
                />
              )}
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                Descrizione dettagliata
              </label>
              <textarea
                rows={2}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444] resize-none"
                value={nc.note}
                onChange={(e) => setNc((p) => ({ ...p, note: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                Azioni correttive
              </label>
              <select
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444] appearance-none"
                value={nc.azione_correttiva}
                onChange={(e) =>
                  setNc((p) => ({ ...p, azione_correttiva: e.target.value }))
                }
              >
                <option value="RESTITUZIONE_FORNITORE">
                  RESTITUZIONE AL FORNITORE
                </option>
                <option value="CONFINAMENTO">CONFINAMENTO DELLA MERCE</option>
                <option value="ELIMINAZIONE_SCARTO">
                  ELIMINAZIONE / SCARTO
                </option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                Verifica dell’azione correttiva
              </label>
              <textarea
                rows={2}
                placeholder="Note sulla chiusura della non conformità…"
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#EF4444] resize-none"
                value={nc.verifica_azione}
                onChange={(e) =>
                  setNc((p) => ({ ...p, verifica_azione: e.target.value }))
                }
              />
            </div>

            <div className="flex flex-col md:flex-row gap-3">
              <button
                type="submit"
                disabled={saving}
                className="flex-1 bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold text-lg py-4 rounded-[20px] shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 disabled:opacity-60 disabled:transform-none"
              >
                <AlertOctagon className="w-6 h-6" />
                {saving ? "Salvataggio..." : "Registra NC"}
              </button>

              <button
                type="button"
                onClick={loadList}
                className="h-[56px] md:h-auto px-5 rounded-[20px] border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2 shadow-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Aggiorna
              </button>
            </div>
          </form>
        </div>
      </section>

      <section className="lg:col-span-5 space-y-6">
        <div className="bg-white rounded-[24px] p-6 shadow-sm border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-bold text-gray-800">
              Registro Non Conformità
            </h3>
          </div>

          {loading ? (
            <div className="text-sm text-gray-400">Caricamento…</div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-[20px] border border-dashed border-gray-200 text-gray-500">
              Nessuna registrazione.
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className={[
                    "p-4 rounded-[20px] border-2 bg-white relative overflow-hidden",
                    r.tipo_nc === "SCADENZA"
                      ? "border-orange-400"
                      : "border-[#EF4444]",
                  ].join(" ")}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-bold text-gray-800">
                      {r.fornitore || "—"}
                    </div>
                    <span className="text-xs font-mono text-gray-400">
                      {String(r.created_at || "")
                        .slice(0, 10)
                        .split("-")
                        .reverse()
                        .join("/")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span
                      className={[
                        "px-2 py-1 rounded-lg text-[10px] font-bold border flex items-center gap-1",
                        badge(r.tipo_nc),
                      ].join(" ")}
                    >
                      <Thermometer className="w-3 h-3" />
                      {tipoLabel(r.tipo_nc, r.tipo_nc_altro)}
                    </span>
                    <span className="text-xs text-gray-500">
                      DDT {r.ddt || "—"}
                    </span>
                  </div>

                  <div className="text-xs text-gray-500">
                    Azione:{" "}
                    <span className="font-medium text-gray-700">
                      {azioneLabel(r.azione_correttiva)}
                    </span>
                  </div>

                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => openPdf(r.id)}
                      className="h-10 px-4 rounded-[16px] bg-[#EF4444] text-white font-bold hover:bg-[#DC2626] flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                    <button
                      type="button"
                      onClick={() => openLabel(r.id)}
                      className="h-10 px-4 rounded-[16px] border border-red-200 text-red-600 bg-white hover:bg-red-50 flex items-center gap-2 font-bold"
                    >
                      <Tag className="w-4 h-4" />
                      Etichetta
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-4">
            Nota: PDF ed etichetta vengono generati con carta intestata/branding.
          </p>
        </div>
      </section>
    </div>
  );
}

/* ------------------------- UI Helpers ------------------------- */

function InputSoft({ value, onChange, placeholder }) {
  return (
    <input
      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] transition-all"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
    />
  );
}

function UltimeRegistrazioniCard({ records, onViewAll, onCreateNc }) {
  const openPdf = (ncId) =>
    window.open(`/api/merci_non_conformi_pdf_one.php?id=${ncId}`, "_blank");
  const openLabel = (ncId) =>
    window.open(`/api/merci_non_conformi_label_pdf.php?id=${ncId}`, "_blank");

  return (
    <div className="bg-white rounded-[24px] p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-bold text-gray-800">
          Ultime registrazioni
        </h3>
        <button
          type="button"
          className="text-sm text-[#003B71] font-medium hover:underline"
          onClick={onViewAll}
        >
          Vedi tutto
        </button>
      </div>

      {records.length === 0 ? (
        <div className="text-center py-10 bg-gray-50 rounded-[20px] border border-dashed border-gray-200 text-gray-500">
          Nessuna registrazione trovata.
        </div>
      ) : (
        <div className="space-y-4">
          {records.map((r, idx) => {
            const isOk = r.esito === "CONFORME";
            const dateShort = (String(r.data || "").slice(5, 10) || "").replace(
              "-",
              "/"
            );
            const hasNc = !isOk && !!r.nc_id;

            return (
              <div
                key={`${r.id || idx}-${idx}`}
                className={[
                  "p-4 rounded-[20px] border hover:border-[#003B71]/30 transition-all bg-white",
                  !isOk
                    ? "border-red-200 bg-red-50/20"
                    : "border-gray-100 hover:bg-blue-50/30",
                ].join(" ")}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <div
                      className={[
                        "w-8 h-8 rounded-full flex items-center justify-center",
                        isOk
                          ? "bg-[#E0F7F2] text-[#059669]"
                          : "bg-[#FFE9E9] text-[#EF4444]",
                      ].join(" ")}
                    >
                      {isOk ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </div>
                    <div>
                      <div className="font-bold text-gray-800">
                        {r.fornitore || "—"}
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase font-bold tracking-wide">
                        DDT {r.ddt || "—"}
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 font-mono">
                    {dateShort || "—"}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-bold">
                    {r.categoria || r.categoria_conservazione || "—"}
                  </span>

                  {r.tipologia_prodotto ? (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-bold">
                      {r.tipologia_prodotto}
                    </span>
                  ) : null}

                  {r.temperatura !== null &&
                    r.temperatura !== undefined &&
                    r.temperatura !== "" ? (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-lg text-[10px] font-bold">
                      {r.temperatura}°C
                    </span>
                  ) : null}

                  {!isOk ? (
                    <span className="px-2 py-1 bg-red-50 text-[#EF4444] rounded-lg text-[10px] font-bold border border-red-100">
                      Non conforme
                    </span>
                  ) : null}
                </div>

                {!isOk ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hasNc ? (
                      <>
                        <button
                          type="button"
                          onClick={() => openPdf(r.nc_id)}
                          className="h-10 px-4 rounded-[16px] bg-[#EF4444] text-white font-bold hover:bg-[#DC2626] flex items-center gap-2"
                        >
                          <Download className="w-4 h-4" />
                          PDF NC
                        </button>
                        <button
                          type="button"
                          onClick={() => openLabel(r.nc_id)}
                          className="h-10 px-4 rounded-[16px] border border-red-200 text-red-600 bg-white hover:bg-red-50 flex items-center gap-2 font-bold"
                        >
                          <Tag className="w-4 h-4" />
                          Etichetta
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onCreateNc?.(r)}
                        className="h-10 px-4 rounded-[16px] border border-red-200 text-red-700 bg-white hover:bg-red-50 flex items-center gap-2 font-bold"
                        title="Crea e collega la Non Conformità a questa registrazione"
                      >
                        <AlertOctagon className="w-4 h-4" />
                        Crea NC
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StatsMeseCard({ records }) {
  const total = records.length;
  const conformi = records.filter((r) => r.esito === "CONFORME").length;
  const scarti = total - conformi;

  return (
    <div className="bg-[#FA8216] rounded-[24px] p-6 text-white shadow-lg relative overflow-hidden">
      <div className="relative z-10">
        <div className="text-sm opacity-80 mb-1">
          Riepilogo rapido (ultime registrazioni)
        </div>
        <div className="text-4xl font-bold mb-4">{total}</div>
        <div className="flex gap-4">
          <div>
            <div className="text-xs opacity-60 uppercase tracking-wider">
              Conformi
            </div>
            <div className="font-bold text-xl text-white">{conformi}</div>
          </div>
          <div className="w-px bg-white/20" />
          <div>
            <div className="text-xs opacity-60 uppercase tracking-wider">
              Non conformi
            </div>
            <div className="font-bold text-xl text-white">{scarti}</div>
          </div>
        </div>
      </div>

      <Truck className="absolute -bottom-4 -right-4 w-32 h-32 text-white opacity-10" />
    </div>
  );
}
